import type NVL from '@neo4j-nvl/base';
import type { Node, Relationship } from '@neo4j-nvl/base';
import type { CypherProperty } from '@nx/neo4j-sdk';
import { createContext, useContext } from 'react';

export type GraphSelection = { nodeIds: string[]; relationshipIds: string[] };
export type Sidepanel = {
  contents: React.ReactNode;
  isSidePanelOpen: boolean;
  setIsSidePanelOpen: (isSidePanelOpen: boolean) => void;
  sidePanelWidth: number | undefined;
  onSidePanelResize: (width: number) => void;
  minWidth?: number;
};

export type NvlGraph = {
  nodes: Node[];
  rels: Relationship[];
  dataLookupTable: DataLookupTable;
};

export type GraphItemMetaData = { totalCount: number; mostCommonColor: string };
export type DataLookupTable = {
  nodes: Record<string, NodeData>;
  relationships: Record<string, RelData>;
  labels: string[];
  types: string[];
  labelMetaData: Record<string, GraphItemMetaData>;
  typeMetaData: Record<string, GraphItemMetaData>;
};

export type NodeData = {
  labelsSorted: string[];
  properties: Record<string, string>;
  propertyTypes: Record<string, string>;
  color: string;
  id: string;
};
export type RelData = {
  type: string;
  properties: Record<string, string>;
  propertyTypes: Record<string, string>;
  color: string;
  id: string;
};

export type NeoNode = Node & { properties: Record<string, CypherProperty>; labels: string[] };
export type NeoRel = Relationship & { properties: Record<string, CypherProperty>; type: string };

export const GraphVizContext = createContext<
  | {
      nvlInstance: NVL | null;
      sidepanel: Sidepanel | null;
      gesture: Gesture;
      setGesture?: (g: Gesture) => void;
      interactionMode: InteractionMode;
      selected: GraphSelection;
      nvlGraph: NvlGraph;
    }
  | undefined
>(undefined);

export const useGraphVizContext = () => {
  const context = useContext(GraphVizContext);
  if (!context) {
    throw new Error('useGraphVizContext must be used within a GraphVizContext');
  }
  return context;
};

const GESTURES = ['single', 'box', 'lasso'] as const;
export type Gesture = (typeof GESTURES)[number];

const INTERACTION_MODES = ['select', 'draw', 'edit-text', 'pan', 'drag', 'none'] as const;
export type InteractionMode = (typeof INTERACTION_MODES)[number];
